// src/SettingsPage.jsx
import { useState, useEffect, useRef } from "react";
import React from "react";
import useAuthUser from "./useAuthUser";
import { updateProfile, updateEmail, sendPasswordResetEmail } from "firebase/auth";
import {
  doc, getDoc, setDoc,
  collection, addDoc, onSnapshot, query, where,
  serverTimestamp, deleteDoc
} from "firebase/firestore";
import { auth } from "./firebase.js";
import { db } from "./firebase.js";
import { firebaseConfig } from "./firebase";
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import { sendEmailVerification, reload } from "firebase/auth";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import PlaidLinkButton from "./PlaidLinkButton.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { CURRENT_TERMS_VERSION, CURRENT_TERMS_EFFECTIVE } from "./termsConfig";
import { Link } from "react-router-dom";

const PRIMARY = "#00B074"; // Trelli green
const ACCENT = "#F4C542";  // Gold

// Cache sample rows per accountId to prevent "Loading…" flashes on remount
const INLINE_SAMPLE_CACHE = new Map();  // accountId -> array of rows

// After: const ACCENT = "#F4C542";
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.matchMedia(`(max-width:${breakpoint}px)`).matches : false
  );
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(`(max-width:${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
}

// --- Phone helpers (simple, US-first) ---
function normalizeE164(input) {
  if (!input) return "";
  const s = String(input).trim();

  // Already E.164-ish: + and digits only
  if (/^\+\d{8,15}$/.test(s)) return s;

  // Strip all non-digits
  const digits = s.replace(/\D+/g, "");

  // US handling:
  //  - 10 digits → assume US and prefix +1
  //  - 11 digits starting with 1 → treat as US and prefix +
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;

  // Fallback: if it *looks* like an international number without + (8–15 digits), add +
  if (digits.length >= 8 && digits.length <= 15) return `+${digits}`;

  return ""; // invalid
}

function isValidE164(input) {
  return /^\+\d{8,15}$/.test(String(input || "").trim());
}

function TopTabs({ tab, setTab }) {
  const isMobile = useIsMobile(); 
  const tabs = [
    { key: "account", label: "Account" },
    { key: "users", label: "Users" },
    { key: "plaid", label: "Plaid Connections" },
    { key: "notifications", label: "Notifications" },
  ];
    return (
      <div style={{
        display: "flex",
        gap: isMobile ? "0.25rem" : "1rem",
        alignItems: "center",
        borderBottom: "1px solid #e6e6e6",
        marginBottom: "1rem",
        paddingBottom: "0.5rem",
        overflowX: isMobile ? "auto" : "visible",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin"
      }}>
      {tabs.map(t => {
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              border: "none",
              background: "transparent",
              fontSize: isMobile ? "0.95rem" : "1rem",
              padding: isMobile ? "0.5rem 0.5rem" : "0.5rem 0.75rem",
              marginBottom: "-2px",
              borderBottom: active ? `3px solid ${PRIMARY}` : "3px solid transparent",
              color: t.key === "users" && !USERS_FEATURE_ENABLED ? "#999" : (active ? PRIMARY : "#333"),
              cursor: t.key === "users" && !USERS_FEATURE_ENABLED ? "not-allowed" : "pointer",
              title: t.key === "users" && !USERS_FEATURE_ENABLED ? "Coming soon" : undefined,
              fontWeight: active ? 700 : 500,
              flex: isMobile ? "0 0 auto" : "0 0 auto",
              minWidth: isMobile ? 110 : undefined,  // bigger touch target
              borderRadius: isMobile ? 8 : 0
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function OnboardingMfaHint({ user }) {
  const [ud, setUd] = useState(null);

  useEffect(() => {
    if (!user?.uid) { setUd(null); return; }
    const unsub = onSnapshot(doc(db, "users", user.uid), snap =>
      setUd(snap.exists() ? snap.data() : {})
    );
    return () => unsub();
  }, [user?.uid]);

  if (ud?.onboarding?.setupStep === "mfa") {
    return (
      <div style={{ marginTop: 8, color: "#475467" }}>
        <strong>Required:</strong> Enable two-factor authentication to continue. Click <em>Manage 2FA</em> to enroll your phone.
      </div>
    );
  }
  return null;
}

/** --------------------
 * Account Section
 * (Scaffold only — wiring to Firebase/Auth comes later)
 * -------------------- */
function AccountSection() {

  const isMobile = useIsMobile();

  const [show2FA, setShow2FA] = useState(false);
  
  const { user, loading } = useAuthUser();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // managed via MFA later
  const [phoneErr, setPhoneErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // debug
  const [showDebug, setShowDebug] = useState(false);
  const [debugAuth, setDebugAuth] = useState(null);
  const [debugFS, setDebugFS] = useState(null);

  // Initialize fields from the user once it loads; pull from Auth then fall back to Firestore
  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (loading) return;

      console.log("[AccountSection] loading:", loading, "user:", user);
      setDebugAuth(user ? {
        uid: user.uid,
        email: user.email || null,
        displayName: user.displayName || null,
        phoneNumber: user.phoneNumber || null,
        providerData: user.providerData?.map(p => ({ providerId: p.providerId, email: p.email })) || [],
      } : null);

      if (!user) return;

      const authDisplay = user.displayName || "";
      const authEmail = user.email || "";
      const authPhone = user.phoneNumber || "";

      if (!cancelled) {
        setDisplayName(authDisplay);
        setEmail(authEmail);
        setPhone(authPhone);
      }

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          setDebugFS(data);
          console.log("[AccountSection] Firestore users doc:", data);

          // Only fill from Firestore if Auth value is missing
          if (!cancelled) {
            if (!authDisplay && (data.displayName || data.username)) {
              setDisplayName(data.displayName || data.username || "");
            }
            if (!authEmail && data.email) {
              setEmail(data.email || "");
            }
            if (!authPhone && (data.phone || data.phoneNumber)) {
              setPhone(data.phone || data.phoneNumber || "");
            }
          }
        } else {
          setDebugFS({ _missing: true });
          console.log("[AccountSection] Firestore users doc missing");
        }
      } catch (e) {
        console.warn("Failed to read Firestore user profile:", e);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [loading, user]);

  if (loading) return <div style={cardStyle}>Loading account…</div>;
  if (!user) {
    return (
      <div style={cardStyle}>
        <h3 style={h3}>Not signed in</h3>
        <p>Please sign in to manage your account.</p>
      </div>
    );
  }

  async function mirrorToFirestore() {
    // Keep Firestore in sync with what we show/save here
    try {
      await setDoc(
        doc(db, "users", user.uid),
        {
          // Use the names you prefer in your schema
          displayName: displayName || null,
          email: email || null,
          phone: phone || null,
          updatedAt: new Date(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn("Failed to write user profile to Firestore:", e);
    }
  }

  async function handleSaveProfile() {
    setSaving(true); setMsg(null); setErr(null);
    try {
      // Normalize and validate phone (for Notifications)
      const normPhone = phone ? normalizeE164(phone) : "";
      if (phone && !isValidE164(normPhone)) {
        setPhoneErr("Please enter a valid phone. Example: +15551234567 or 678-555-1234");
        throw new Error("Invalid phone format");
      }  
      // Update Auth first
      if (displayName !== (user.displayName || "")) {
        await updateProfile(user, { displayName });
      }
      if (email !== (user.email || "")) {
        await updateEmail(user, email); // may require recent login
      }

      // Then mirror to Firestore so Settings remembers it
      await setDoc(
        doc(db, "users", user.uid),
        {
            displayName: displayName || null,
            email: email || null,
            phone: normPhone || null,
            updatedAt: new Date(),
        },
        { merge: true }
      );

      setMsg("Profile saved.");
      if (normPhone && normPhone !== phone) setPhone(normPhone);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally { setSaving(false); }
  }

  async function handleResetPassword() {
    setSaving(true); setMsg(null); setErr(null);
    try {
      await sendPasswordResetEmail(auth, email || user.email || "");
      setMsg("Password reset email sent.");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally { setSaving(false); }
  }

  return (
    <div style={cardStyle}>
      <h3 style={h3}>Profile</h3>

      {msg && <div style={noticeOk}>{msg}</div>}
      {err && <div style={noticeErr}>{err}</div>}
      <div style={{ ...grid2, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
        <Field label="Username">
          <input
            style={input}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your display name"
          />
        </Field>

        <Field label="Email">
          <input
            style={input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@email.com"
          />
        </Field>

        <Field label="Phone (for text alerts)">
          <input
            style={input}
            value={phone}
            onChange={(e) => {
                setPhone(e.target.value);
                // clear inline error while typing
                if (phoneErr) setPhoneErr("");
            }}
            onBlur={() => {
                const norm = normalizeE164(phone);
                if (!norm) {
                    setPhoneErr("Please enter a valid phone. Example: +15551234567 or 678-555-1234");
                } else {
                    setPhone(norm);
                    setPhoneErr("");
                }
            }}
            placeholder="+1 555-123-4567"
            title="This is used for alerts; 2FA/verified phone comes in the 2FA step."
          />
          {phoneErr && <small style={{ color: "#b00020" }}>{phoneErr}</small>}
        </Field>
        <div />
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button style={btnPrimary} onClick={handleSaveProfile} disabled={saving}>
          {saving ? "Saving…" : "Save Profile"}
        </button>
        <button style={btnSec} onClick={handleResetPassword} disabled={saving}>
          Send Password Reset
        </button>
        <button
          style={btnHollow}
          onClick={async () => {
            try {
              // Make sure we open the modal with a fully fresh user object
              if (auth.currentUser) {
                await auth.currentUser.reload();
              }
            } catch {}
            setShow2FA(true);
          }}
        >
          Manage 2FA
        </button>
      </div>

      <OnboardingMfaHint user={user} />


      {showDebug && (
        <div style={{ marginTop: 12, background: "#fafafa", border: "1px solid #eee", borderRadius: 8, padding: 10 }}>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify({ authUser: debugAuth, firestoreUser: debugFS }, null, 2)}
          </pre>
        </div>
      )}

      {/* Email verification notice */}
      {user && !user.emailVerified && (
        <div style={{ ...noticeErr, marginBottom: 12 }}>
            <div style={{ marginBottom: 6 }}>
            <strong>Verify your email</strong> to enable two-factor authentication.
            </div>
            <button
            style={btnHollow}
            onClick={async () => {
                try {
                await sendEmailVerification(user);
                setMsg("Verification email sent. Please check your inbox, then refresh this page.");
                } catch (e) {
                setErr(e?.message || String(e));
                }
            }}
            >
            Send verification email
            </button>
        </div>
      )}

      {show2FA && <TwoFAModal open={show2FA} onClose={() => setShow2FA(false)} user={user} />}

      {/* Current Plan */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <h3 style={h3}>Current Plan</h3>
        <div style={{ display:"grid", gap:4 }}>
          <div>
            <div>
              <strong>Plan:</strong>{" "}
              <span style={{ color:"#111" }}>{debugFS?.planLabel || "Unknown"}</span>
            </div>
            <small style={{ color:"#666" }}>
              {(debugFS?.planTier || "unknown").toUpperCase()} • {debugFS?.planBilling || "—"}
            </small>
          </div>
        </div>
      </div>

      {/* Change Plan (coming soon – disabled) */}
      <div style={{ ...cardStyle, marginTop: 12 }}>
        <h3 style={h3}>Change Plan</h3>
        <div style={{ display:"grid", gap:8 }}>
          <div style={{ color:"#666", fontSize:13 }}>
            Upgrade, downgrade, or switch plans. (Coming soon)
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", opacity:0.4, pointerEvents:"none" }}>
            {/* Show all options; current one visually indicated */}
            <button style={btnSec} title="Coming soon">
              {debugFS?.planTier === "free" ? "Free (Manual) • Current" : "Free (Manual)"}
            </button>
            <button style={btnSec} title="Coming soon">
              {debugFS?.planTier === "beta" ? "Beta • Current" : "Beta"}
            </button>
            <button style={btnSec} title="Coming soon">
              {debugFS?.planTier === "standard" ? "Standard • Current" : "Standard"}
            </button>
            <button style={btnSec} title="Coming soon">
              {debugFS?.planTier === "family" ? "Family • Current" : "Family"}
            </button>
          </div>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div style={{ ...cardStyle, marginTop: 12 }}>
        <h3 style={h3}>Terms &amp; Conditions</h3>
        <div style={{ background:"#fafafa", border:"1px solid #eee", borderRadius:8, padding:10 }}>
          <div style={{ display:"grid", gap:4 }}>
            <div>
              <strong>Terms accepted:</strong>{" "}
              <span style={{ color:"#111" }}>
                v{debugFS?.legal?.termsVersionAccepted || "—"}
              </span>
              {debugFS?.legal?.termsAcceptedAt && (
                <span style={{ color:"#666" }}>
                  {" "}on{" "}
                  {debugFS.legal.termsAcceptedAt.toDate
                    ? debugFS.legal.termsAcceptedAt.toDate().toLocaleString()
                    : new Date(debugFS.legal.termsAcceptedAt).toLocaleString()
                  }
                </span>
              )}
            </div>
            <div style={{ fontSize:13, color:"#666" }}>
              Current Terms: v{CURRENT_TERMS_VERSION} (effective {new Date(CURRENT_TERMS_EFFECTIVE).toLocaleDateString()}) •{" "}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">View Terms</Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

/** --------------------
 * Users Section
 * -------------------- */
function UsersSection() {

  // MVP: temporarily disabled, show Coming Soon card
  if (!USERS_FEATURE_ENABLED) {
    return (
      <div style={{ ...cardStyle, opacity: 0.6 }}>
        <h3 style={h3}>Household Access <span style={{ fontSize: 12, color: "#888" }}>(Coming Soon)</span></h3>
        <div style={{ marginTop: 8, padding: 12, background: "#fafafa", border: "1px solid #eee", borderRadius: 8 }}>
          We’re polishing Household access so co-heads and family members can be added with the right permissions.
          <br />
          <small style={{ color: "#666" }}>
            For the MVP, please share a login if you need a spouse or partner to view/edit.
          </small>
        </div>
      </div>
    );
  }

  const isMobile = useIsMobile();

  const { user, loading } = useAuthUser();

  const [initBusy, setInitBusy] = useState(true);
  const [householdId, setHouseholdId] = useState("");
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);

  // invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteAccess, setInviteAccess] = useState("edit"); // 'edit' | 'read'
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);

  // test-email helpers and copy-link state
  const [copiedLink, setCopiedLink] = useState("");

  const TEST_EMAIL_REGEX = /@(test\.com|example\.com)$/i;
    function isTestEmail(addr = "") {
    return TEST_EMAIL_REGEX.test(String(addr).trim());
  }
  function buildAcceptUrl(householdId, inviteId, email) {
    const APP_URL = import.meta.env.VITE_APP_URL || window.location.origin;
    return `${APP_URL}/accept-invite?hid=${encodeURIComponent(householdId)}&inv=${encodeURIComponent(inviteId)}&email=${encodeURIComponent(email)}`;
  }

  // --- helpers ---
  function isEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());
  }

  // Ensure household exists & user is a member (Head of Household)
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    (async () => {
      setInitBusy(true);
      setErr(null); setMsg(null);

      // 1) read users/{uid} for existing householdId
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      let hid = userSnap.exists() && userSnap.data().householdId
        ? userSnap.data().householdId
        : "";

      // 2) create household if missing
      if (!hid) {
        // use uid as deterministic household id (simple & stable)
        hid = user.uid;
        const hhRef = doc(db, "households", hid);
        try {
         // Will succeed if creating; if it already exists, update will be denied by rules — we can ignore.
         await setDoc(hhRef, {
           ownerUid: user.uid,
           ownerEmail: user.email || null,
           createdAt: serverTimestamp(),
           updatedAt: serverTimestamp(),
         }, { merge: false });
       } catch (e) {
         // If it existed, this throws due to lack of update permission before you're a member — safe to ignore.
       }
        await setDoc(userRef, { householdId: hid, updatedAt: new Date() }, { merge: true });
      }

      setHouseholdId(hid);

      // 3) ensure current user is listed as a member (role: head)
      const memRef = doc(db, "households", hid, "members", user.uid);
      try {
        await setDoc(memRef, {
            uid: user.uid,
            email: user.email || null,
            role: "head",
            access: "edit",
            createdAt: serverTimestamp(),
        }, { merge: false }); // create-only
      } catch (e) {
        // If member doc already exists, update is not allowed unless head — safe to ignore here.
      }

      // 4) subscribe to members and invites
      const unsubMembers = onSnapshot(
        collection(db, "households", hid, "members"),
        (snap) => {
          setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      );

      const unsubInvites = onSnapshot(
        query(collection(db, "households", hid, "invites")),
        (snap) => {
          setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      );

      setInitBusy(false);
      return () => { unsubMembers(); unsubInvites(); };
    })().catch(e => {
      console.warn(e);
      setErr(e?.message || String(e));
      setInitBusy(false);
    });
  }, [loading, user]);

  if (loading || initBusy) {
    return <div style={cardStyle}>Loading household…</div>;
  }
  if (!user) {
    return (
      <div style={cardStyle}>
        <h3 style={h3}>Not signed in</h3>
        <p>Please sign in to manage household users.</p>
      </div>
    );
  }

  async function sendInvite() {
    setMsg(null); setErr(null); setCopiedLink("");

    const emailRaw = (inviteEmail || "").trim();
    const emailLC = emailRaw.toLowerCase();

    if (!isEmail(emailRaw)) {
        setErr("Please enter a valid email address.");
        return;
    }

    // prevent duplicate: if already member or already invited
    const existsAsMember = members.some(m => (m.email || "").toLowerCase() === emailLC);
    const existsAsInvite = invites.some(i =>
        (i.email || "").toLowerCase() === emailLC &&
        (i.status || "pending") === "pending"
    );
    if (existsAsMember) { setErr("That email already has access."); return; }
    if (existsAsInvite) { setErr("An invite is already pending for that email."); return; }

    try {
        setSending(true);

        // 1) Create invite (store lower-cased email for consistent matching)
        const inviteRef = await addDoc(collection(db, "households", householdId, "invites"), {
        email: emailLC,
        role: "coHead",
        access: inviteAccess,        // 'edit' | 'read'
        invitedByUid: user.uid,
        invitedByEmail: user.email || null,
        status: "pending",           // 'pending' | 'accepted' | 'revoked'
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        });

        // 2) Build accept URL
        const acceptUrl = buildAcceptUrl(householdId, inviteRef.id, emailLC);

        // 3) Delivery strategy:
        //    - For test domains (@test.com, @example.com) → don't send email; show a copyable link instead
        //    - For real emails → enqueue email via Trigger Email extension
        if (isTestEmail(emailLC)) {
        setMsg("Test invite created. Copy the link below and open it as the invited user.");
        setCopiedLink(acceptUrl);
        } else {
        await addDoc(collection(db, "mail"), {
            to: [emailRaw], // use original casing for the email delivery
            message: {
            subject: "You’ve been invited to Trelli (Co-Head of Household)",
            text: `You were invited to join a Trelli household with ${user.email || "a Trelli user"}.\n\nAccept: ${acceptUrl}\n\nIf you weren’t expecting this, ignore this email.`,
            html: `
                <p>You were invited to join a Trelli household with <strong>${user.email || "a Trelli user"}</strong>.</p>
                <p><a href="${acceptUrl}" target="_blank" rel="noopener noreferrer"
                style="background:#00B074;color:#fff;padding:10px 14px;border-radius:8px;text-decoration:none;font-weight:700">
                Accept invite
                </a></p>
                <p style="color:#666">If you weren’t expecting this, you can ignore this email.</p>
            `,
            },
        });
        setMsg("Invite created and email sent.");
        }

        setInviteEmail("");
    } catch (e) {
        console.error("[Users] invite create/send failed", e);
        setErr(e?.message || String(e));
    } finally {
        setSending(false);
    }
  }

  async function revokeInvite(id) {
    try {
      await deleteDoc(doc(db, "households", householdId, "invites", id));
      setMsg("Invite revoked.");
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  return (
    <div style={cardStyle}>
      <h3 style={h3}>Household Access</h3>

      {msg && <div style={noticeOk}>{msg}</div>}
      {err && <div style={noticeErr}>{err}</div>}

      <div style={{ marginBottom: 12 }}>
        <div><strong>Head of Household</strong> (you)</div>
        <small>You are the primary account owner.</small>
      </div>

      <div style={divider} />

      <h4 style={h4}>Co-Head of Household</h4>
      <div style={{ ...grid2, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
        <Field label="Invite Co-Head (email)">
          <input
            style={input}
            placeholder="partner@email.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
        </Field>
        <Field label="Access Level">
          <select
            style={input}
            value={inviteAccess}
            onChange={(e) => setInviteAccess(e.target.value)}
          >
            <option value="edit">Edit</option>
            <option value="read">Read only</option>
          </select>
        </Field>
      </div>
      <div style={{ marginTop: 8 }}>
        <button style={btnPrimary} disabled={sending} onClick={sendInvite}>
          {sending ? "Sending…" : "Send Invite"}
        </button>
      </div>

      {copiedLink && (
        <div style={{ marginTop: 10, padding: 10, border: "1px solid #eee", borderRadius: 8 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Test invite link</div>
            <div style={{ wordBreak: "break-all", fontSize: 13 }}>{copiedLink}</div>
            <button
            style={{ marginTop: 8, background: "#F4C542", color: "#000", border: "none", padding: "8px 12px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
            onClick={async () => {
                try { await navigator.clipboard.writeText(copiedLink); setMsg("Link copied!"); } catch {}
            }}
            >
            Copy link
            </button>
        </div>
      )}

      <div style={{ height: 16 }} />

      {/* Current members */}
      <h4 style={h4}>Current Members</h4>
      {members.length === 0 ? (
        <div style={{ background:"#fafafa", padding:12, borderRadius:8, border:"1px solid #eee" }}>
          No members yet.
        </div>
      ) : (
        <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:8 }}>
          {members.map(m => (
            <li key={m.id} style={{ border:"1px solid #eee", borderRadius:8, padding:"10px 12px", display:"grid", gridTemplateColumns:"1fr auto", alignItems:"center", gap:10 }}>
              <div>
                <div><strong>{m.email || "(no email)"}</strong></div>
                <small>Role: {m.role} · Access: {m.access}</small>
              </div>
              {m.role !== "head" && (
                <button style={btnHollow} title="(Future) Remove member">
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div style={{ height: 16 }} />

      {/* Pending invites */}
      <h4 style={h4}>Pending Invites</h4>
      {invites.length === 0 ? (
        <div style={{ background:"#fafafa", padding:12, borderRadius:8, border:"1px solid #eee" }}>
          None pending.
        </div>
      ) : (
        <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:8 }}>
          {invites.map(inv => (
            <li key={inv.id} style={{ border:"1px solid #eee", borderRadius:8, padding:"10px 12px", display:"grid", gridTemplateColumns:"1fr auto", alignItems:"center", gap:10 }}>
              <div>
                <div><strong>{inv.email}</strong></div>
                <small>Status: {inv.status || "pending"} · Role: {inv.role} · Access: {inv.access}</small>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                    style={btnHollow}
                    onClick={async () => {
                    const url = buildAcceptUrl(householdId, inv.id, inv.email);
                    try { await navigator.clipboard.writeText(url); setMsg("Invite link copied."); } catch {}
                    }}
                    title="Copy accept link"
                >
                    Copy link
                </button>
                <button style={btnHollow} onClick={() => revokeInvite(inv.id)}>
                    Revoke
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div style={{ height: 16 }} />
      <div style={{ background: "#fafafa", padding: "12px 14px", borderRadius: 8, border: "1px dashed #ddd" }}>
        <strong>Children & Other Family Members</strong> — <em>Coming Soon</em>
        <div style={{ marginTop: 4 }}>
          <small>Granular access to specific budgets and reports.</small>
        </div>
      </div>
    </div>
  );
}

/** --------------------
 * Notifications Section
 * (Scaffold the matrix of channels x types; defaults set)
 * -------------------- */
function NotificationsSection() {
  const { user, loading } = useAuthUser();

  const isMobile = useIsMobile();

  // UI state
  const [initBusy, setInitBusy] = useState(true);
  const [enabled, setEnabled] = useState(true);        // on/off for the single MVP alert
  const [emailOverride, setEmailOverride] = useState(""); // optional “send to” email
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // Onboarding doc (for setupStep === "notifications")
  const [userDoc, setUserDoc] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { setUserDoc(null); return; }
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      setUserDoc(snap.exists() ? snap.data() : {});
    });
    return () => unsub();
  }, [loading, user]);

  // simple email validator (optional field)
  function isEmail(s) {
    if (!s) return true; // empty = allowed (use account email)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());
  }

  // Load existing prefs from Firestore
  useEffect(() => {
    if (loading) return;
    if (!user) { setInitBusy(false); return; }

    (async () => {
      setInitBusy(true);
      setErr(null); setMsg(null);

      try {
        // Store under users/{uid}/settings/notifications
        const ref = doc(db, "users", user.uid, "settings", "notifications");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const d = snap.data() || {};
          setEnabled(d.dailyDigestEnabled ?? true);
          setEmailOverride(d.emailOverride ?? "");
        } else {
          // default: enabled, no override
          setEnabled(true);
          setEmailOverride("");
        }
      } catch (e) {
        setErr(e?.message || String(e));
      } finally {
        setInitBusy(false);
      }
    })();
  }, [loading, user]);

  async function handleSave() {
    if (!user) return;
    setSaving(true); setMsg(null); setErr(null);

    const trimmed = (emailOverride || "").trim();
    if (!isEmail(trimmed)) {
      setErr("Please enter a valid email address, or leave it blank.");
      setSaving(false);
      return;
    }

    try {
      const ref = doc(db, "users", user.uid, "settings", "notifications");
      await setDoc(ref, {
        dailyDigestEnabled: !!enabled,
        emailOverride: trimmed || null,
        updatedAt: new Date(),
      }, { merge: true });

      setMsg("Notification settings saved.");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading || initBusy) {
    return <div style={cardStyle}>Loading notifications…</div>;
  }
  if (!user) {
    return (
      <div style={cardStyle}>
        <h3 style={h3}>Notifications</h3>
        <p>Please sign in to manage notifications.</p>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h3 style={h3}>Daily Alert Email</h3>
      <p style={{ marginTop: 0, color: "#555" }}>
        We’ll send an email on days when you have transactions that need action,
        or when budgets are near/over or off pace. If nothing needs attention, no email is sent.
      </p>

      {msg && <div style={noticeOk}>{msg}</div>}
      {err && <div style={noticeErr}>{err}</div>}

      <div style={{ display: "grid", gap: 10, maxWidth: isMobile ? "100%" : 520 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span><strong>Enable the daily alert email</strong></span>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#666" }}>Send to (optional)</span>
          <input
            style={input}
            type="email"
            placeholder="name@work.com (leave blank to use your account email)"
            value={emailOverride}
            onChange={(e) => setEmailOverride(e.target.value)}
          />
          <small style={{ color: "#666" }}>
            If blank, we’ll send to your Trelli account email.
          </small>
        </label>
      </div>

      <div style={{ height: 12 }} />
      <button style={btnPrimary} onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Save Notification Settings"}
      </button>

      <div style={{ marginTop: 14, padding: 12, background: "#fafafa", border: "1px solid #eee", borderRadius: 8 }}>
        <strong>More alerts are coming soon</strong> — text and push notifications, plus additional report types.
      </div>

      {/* Onboarding continue (only during setup: notifications) */}
      {userDoc?.onboarding?.setupStep === "notifications" && (
        <div style={{ marginTop: 16 }}>
          <button
            onClick={async () => {
              if (!user) return;
              await setDoc(
                doc(db, "users", user.uid),
                { onboarding: { ...(userDoc.onboarding || {}), ackNotifications: true } },
                { merge: true }
              );
            }}
            style={{
              height: 40, borderRadius: 10, border: "none",
              background: "#00B074", color: "#fff", fontWeight: 800, padding: "0 16px", cursor: "pointer"
            }}
          >
            Continue
          </button>
          <div style={{ marginTop: 8, color: "#475467", fontSize: 13 }}>
            You can change notification email anytime here later.
          </div>
        </div>
      )}

    </div>
  );
}

function PlaidConnectionsSection() {
  const { user, loading } = useAuthUser();
  const isMobile = useIsMobile();
  const [initBusy, setInitBusy] = useState(true);
  const [items, setItems] = useState([]); // [{id, ...data, accounts:[...]}]
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [activeRebuildAccountId, setActiveRebuildAccountId] = useState(null);
  const [activeRebuildJob, setActiveRebuildJob] = useState(null);

  const [flipOpenFor, setFlipOpenFor] = useState(null); // { item, account }
  const [inlineOpen, setInlineOpen] = useState({});     // { [accountId]: boolean }

  function toggleInline(aid) {
    setInlineOpen(prev => ({ ...prev, [aid]: !prev[aid] }));
  }

  // Calls the backend to (re)pull Plaid accounts for all items and write them under:
  // users/{uid}/plaid/accounts/byId/{account_id}
  const BACKFILL_URL =
    "https://us-central1-trelli-prod-473722.cloudfunctions.net/plaidBackfill";

  const OFFBOARD_URL =
  "https://us-central1-trelli-prod-473722.cloudfunctions.net/removePlaidItem";

  const [backfillBusy, setBackfillBusy] = useState(false);

  // Prevent repeated auto-backfills during an OAuth return
  const autoBackfilledRef = useRef(false);

  // ---------- UI helpers ----------
  function fmtMoney(n, ccy = "USD") {
    const v = typeof n === "number" ? n : Number(n || 0);
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(v); }
    catch { return `${v.toFixed(2)} ${ccy || ""}`.trim(); }
  }
  function fmtWhen(ts) {
    try {
      if (!ts) return "—";
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString();
    } catch { return "—"; }
  }
  function pickBalance(balances = {}) {
    // Prefer 'available' when present, otherwise 'current'
    if (typeof balances.available === "number") return { amount: balances.available, label: "available" };
    if (typeof balances.current === "number")   return { amount: balances.current,   label: "current" };
    return { amount: null, label: "" };
  }
  function statusPill(status, code) {
    if (status === "ok") return <span style={{ background:"#f0fff6", color:"#0f6848", border:"1px solid #c8f1de", padding:"2px 8px", borderRadius:999, fontSize:12 }}>Connected</span>;
    if (status === "error") return <span style={{ background:"#fff5f5", color:"#8a1f1f", border:"1px solid #ffd1d1", padding:"2px 8px", borderRadius:999, fontSize:12 }}>Needs attention{code ? ` (${code})` : ""}</span>;
    return <span style={{ background:"#f7f7f7", color:"#555", border:"1px solid #e5e5e5", padding:"2px 8px", borderRadius:999, fontSize:12 }}>Unknown</span>;
  }

  function isFreshItem(it) {
    const now = Date.now();
    const ts =
      (it?.updatedAt?.toDate && it.updatedAt.toDate()) ||
      (it?.lastHealthyAt?.toDate && it.lastHealthyAt.toDate()) ||
      null;
    if (!ts) return false;
    return now - ts.getTime() < 5 * 60 * 1000;
  }

  async function refreshAccounts() {
    try {
      setErr("");
      setMsg("");
      setBackfillBusy(true);

      const currentUser = auth.currentUser;
      if (!currentUser) {
        setErr("You must be logged in to refresh accounts.");
        return;
      }
      const idToken = await currentUser.getIdToken();

      const res = await fetch(BACKFILL_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        // Optional: you can tweak the range; backend clamps it (1..730 days)
        body: JSON.stringify({ days: 180 }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Backfill failed with ${res.status}`);
      }

      // Your onSnapshot listeners will auto-update when docs are written.
      setMsg("Accounts refreshed.");
    } catch (e) {
      console.error("plaidBackfill error:", e);
      setErr("Failed to refresh accounts. Please try again.");
    } finally {
      setBackfillBusy(false);
    }
  }

  async function handleAfterFlipSigns({ accountId, sign, scope }) {
    // Future-only means “don’t rewrite history”
    if (scope === "future") {
      // Don’t backfill here. Account doc already updated; snapshots will refresh UI.
      console.log("[Settings flip] future-only saved (no rebuild job)", { accountId, sign });
      return;
    }

    const u = auth.currentUser;
    if (!u?.uid || !accountId) return;

    const idToken = await u.getIdToken();

    // Fire-and-forget: starts the backend job and returns immediately
    const url =
      "https://us-central1-trelli-prod-473722.cloudfunctions.net/startNormalizationRebuild";

    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        accountId,
        reason: "signFlip",
      }),
    });

    const json = await resp.json().catch(() => ({}));

    if (!resp.ok || !json?.ok) {
      console.error("[Settings flip] start job failed", { status: resp.status, json });
      throw new Error(json?.error || `startNormalizationRebuild failed: ${resp.status}`);
    }

    console.log("[Settings flip] rebuild job started", {
      accountId,
      sign,
      scope,
      jobPath: json.jobPath,
    });
    setActiveRebuildAccountId(accountId);

    try {
      localStorage.setItem(`trelli:activeNormalizationRebuild:${u.uid}`, accountId);
      window.dispatchEvent(new Event("trelli:activeNormalizationRebuild"));
    } catch {}

    // Optional: if you add UI banner support later
    // setActiveRebuildJobPath(json.jobPath);
  }

  // --- Return this account to automatic sign detection (removes durable override) ---
  async function revertToAutomatic(accountId) {
    if (!accountId) return;
    const userNow = auth.currentUser;
    if (!userNow?.uid) { setErr("Not signed in."); return; }

    try {
      setBackfillBusy(true);

      // 1) Remove the durable override (stops the guard from reasserting a sign)
      await deleteDoc(doc(db, "users", userNow.uid, "plaid", "accounts", "overrides", accountId));

      // 2) Clear sticky flag on the account doc (do NOT set signNormalization here)
      await setDoc(
        doc(db, "users", userNow.uid, "plaid", "accounts", "byId", accountId),
        { userOverridden: false, updatedAt: new Date() },
        { merge: true }
      );

      // 3) Soft refresh the UI so account snapshots update
      await refreshAccounts();
    } catch (e) {
      console.error("[Settings] revertToAutomatic failed", e);
      setErr(e?.message || String(e));
    } finally {
      setBackfillBusy(false);
    }
  }

  useEffect(() => {
    if (loading) return;
    if (!user) { setInitBusy(false); return; }

    setInitBusy(true);
    setErr(""); setMsg("");

    // Subscribe to items under users/{uid}/plaid/items (one doc per Plaid item)
    const unsubItems = onSnapshot(
      collection(db, "users", user.uid, "plaid", "items", "byId"),
      async (snap) => {
        const base = snap.docs.map(d => ({ id: d.id, ...d.data(), accounts: [] }));

        // For each item, load its accounts from /users/{uid}/plaid/accounts/byId
        // filtered by item_id == current item.id
        const { getDocs, query: fsQuery, where } = await import("firebase/firestore");

        const withAccounts = await Promise.all(
          base.map(async (it) => {
            try {
              // First try the canonical field
              let accSnap = await getDocs(fsQuery(
                collection(db, "users", user.uid, "plaid", "accounts", "byId"),
                where("item_id", "==", it.id)
              ));
              let accounts = accSnap.docs.map(a => ({ id: a.id, ...a.data() }));

              // Fallback: older writers may have used owner_item_id
              if (accounts.length === 0) {
                const accSnap2 = await getDocs(fsQuery(
                  collection(db, "users", user.uid, "plaid", "accounts", "byId"),
                  where("owner_item_id", "==", it.id)
                ));
                const accounts2 = accSnap2.docs.map(a => ({ id: a.id, ...a.data() }));
                if (accounts2.length) accounts = accounts2;
              }

              return { ...it, accounts };
            } catch {
              return it;
            }
          })
        );

        // --- sort accounts within each item (by name, then mask) ---
        withAccounts.forEach((it) => {
          it.accounts = (Array.isArray(it.accounts) ? [...it.accounts] : []).sort((a, b) => {
            const an = (a?.name || a?.official_name || "").toLowerCase();
            const bn = (b?.name || b?.official_name || "").toLowerCase();
            if (an < bn) return -1;
            if (an > bn) return 1;
            return String(a?.mask || "").localeCompare(String(b?.mask || ""));
          });
        });

        // --- sort items (banks) by institution_name, then id ---
        withAccounts.sort((a, b) => {
          const an = (a?.institution_name || "").toLowerCase();
          const bn = (b?.institution_name || "").toLowerCase();
          if (an < bn) return -1;
          if (an > bn) return 1;
          return String(a?.id || "").localeCompare(String(b?.id || ""));
        });

        setItems(withAccounts);
        setInitBusy(false);

        // If we just linked (OAuth flow) and the item is fresh but has no accounts yet,
        // automatically trigger a backfill once to populate accounts without navigating away.
        try {
          if (
            !autoBackfilledRef.current &&
            Array.isArray(withAccounts) &&
            withAccounts.some(it => isFreshItem(it) && (!it.accounts || it.accounts.length === 0))
          ) {
            autoBackfilledRef.current = true; // guard first so we don't race
            await refreshAccounts();
          }
        } catch (e) {
          console.warn("[PlaidConnections] auto backfill failed:", e);
        }

      },
      (e) => {
        console.warn(e);
        setErr(e?.message || String(e));
        setInitBusy(false);
      }
    );

    return () => unsubItems();
  }, [loading, user]);

  // Also react to account-level writes so UI updates without navigating away
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    // Subscribe to all accounts for this user
    const unsubAccounts = onSnapshot(
      collection(db, "users", user.uid, "plaid", "accounts", "byId"),
      (snap) => {
        const accs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Recompute each item's accounts from latest account docs
        setItems((prevItems) => {
          const next = prevItems.map((it) => {
            const matches = accs.filter(
              (a) => a.item_id === it.id || a.owner_item_id === it.id
            ).sort((a, b) => {
              const an = (a?.name || a?.official_name || "").toLowerCase();
              const bn = (b?.name || b?.official_name || "").toLowerCase();
              if (an < bn) return -1;
              if (an > bn) return 1;
              return String(a?.mask || "").localeCompare(String(b?.mask || ""));
            });
            return { ...it, accounts: matches };
          });

          next.sort((a, b) => {
            const an = (a?.institution_name || "").toLowerCase();
            const bn = (b?.institution_name || "").toLowerCase();
            if (an < bn) return -1;
            if (an > bn) return 1;
            return String(a?.id || "").localeCompare(String(b?.id || ""));
          });

          return next;
        });
      },
      (e) => {
        console.warn("accounts onSnapshot error:", e);
      }
    );

    return () => unsubAccounts();
  }, [loading, user]);

  // Live job progress banner (normalization rebuild)
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser?.uid || !activeRebuildAccountId) return;

    const jobDocRef = doc(
      db,
      "users",
      currentUser.uid,
      "jobs",
      "normalizationRebuilds",
      "byId",
      activeRebuildAccountId
    );

    const unsub = onSnapshot(
      jobDocRef,
      (snap) => {
        if (!snap.exists()) {
          setActiveRebuildJob(null);
          return;
        }
        const job = snap.data();
        setActiveRebuildJob(job);

        // Auto-clear after completion (optional)
        if (job?.status === "completed") {
          setTimeout(() => {
            setActiveRebuildAccountId(null);
            setActiveRebuildJob(null);
          }, 1500);
        }

        // Keep failed visible (so user sees error) — you can clear manually later
      },
      (e) => {
        console.warn("[Settings] rebuild job onSnapshot error:", e);
      }
    );

    return () => unsub();
  }, [activeRebuildAccountId]);

  // overall empty-state
  const hasAny = items.length > 0;

  // Reflect connection status on the user doc for onboarding gates
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    try {
      setDoc(
        doc(db, "users", user.uid),
        { plaidConnected: !!hasAny, updatedAt: new Date() },
        { merge: true }
      );
    } catch {}
  }, [hasAny, loading, user]);
  
  if (loading || initBusy) return <div style={cardStyle}>Loading Plaid…</div>;
  if (!user) {
    return (
      <div style={cardStyle}>
        <h3 style={h3}>Plaid Connections</h3>
        <p>Please sign in to manage linked accounts.</p>
      </div>
    );
  }

  async function deleteOneItem(itemId) {
    const really = confirm(
      "Remove this bank connection? This will unlink it at the bank (Plaid) and delete your local connection. You can re-link anytime."
    );
    if (!really) return;

    setErr(""); setMsg("");

    try {
      // 0) Auth → ID token for the backend
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setErr("You must be logged in to remove a connection.");
        return;
      }
      const idToken = await currentUser.getIdToken();

      // 1) Tell backend to remove the Item at Plaid and clean server-side data
      const res = await fetch(OFFBOARD_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ item_id: itemId }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Offboarding failed (${res.status})`);
      }

      // 2) Local cleanup to keep UI in sync (optional: keep transactions if you want history)
      const { writeBatch, getDocs, query: fsQuery, where } = await import("firebase/firestore");
      const batch = writeBatch(db);

      // Delete accounts for this item (UI reflects instantly)
      const accQ = fsQuery(
        collection(db, "users", currentUser.uid, "plaid", "accounts", "byId"),
        where("item_id", "==", itemId)
      );
      const accSnap = await getDocs(accQ);
      accSnap.forEach((d) => batch.delete(d.ref));

      /* OPTIONAL: If you prefer to keep historical transactions, delete them too.
      const txQ = fsQuery(
        collection(db, "users", currentUser.uid, "plaid", "transactions", "byId"),
        where("item_id", "==", itemId)
      );
      const txSnap = await getDocs(txQ);
      txSnap.forEach((d) => batch.delete(d.ref));
      */

      // Delete the item doc itself
      batch.delete(
        doc(db, "users", currentUser.uid, "plaid", "items", "byId", itemId)
      );

      await batch.commit();

      setMsg("Connection removed.");
    } catch (e) {
      console.error("[PlaidConnections] deleteOneItem failed", e);
      setErr(e?.message || String(e));
    }
  }

  const InlineSample = React.memo(function InlineSample({ user, accountId, signNormalization = 1 }) {
    // Seed from cache if available so we don't flash a spinner on remounts
    const cached = INLINE_SAMPLE_CACHE.get(accountId) || [];
    const [rows, setRows] = useState(cached);
    const [busy, setBusy] = useState(cached.length === 0);

    useEffect(() => {
      if (!user?.uid || !accountId) return;

      let alive = true;

      (async () => {
        // If we already have cache for this account, don't refetch or show spinner.
        if (INLINE_SAMPLE_CACHE.has(accountId)) return;

        setBusy(true);
        try {
          const { getDocs, query: fsQuery, where, limit, collection } = await import("firebase/firestore");
          const q = fsQuery(
            collection(db, "users", user.uid, "plaid", "transactions", "byId"),
            where("account_id", "==", accountId),
            limit(50)
          );
          const snap = await getDocs(q);
          const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          all.sort((a, b) => {
            const da = a?.date ? new Date(a.date).getTime() : 0;
            const dbt = b?.date ? new Date(b.date).getTime() : 0;
            return dbt - da;
          });
          const slice = all.slice(0, 6);
          if (!alive) return;
          INLINE_SAMPLE_CACHE.set(accountId, slice);   // cache results
          setRows(slice);
        } catch {
          if (!alive) return;
          setRows([]);
        } finally {
          if (alive) setBusy(false);
        }
      })();

      return () => { alive = false; };
    }, [user?.uid, accountId]);

    if (busy) {
      return (
        <div
          style={{
            marginTop: 8,
            background: "#fafafa",
            border: "1px solid #eee",
            borderRadius: 8,
            padding: 8,
            minHeight: 140,
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            color: "#666"
          }}
        >
          Loading…
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div
          style={{
            marginTop: 8,
            background: "#fafafa",
            border: "1px solid #eee",
            borderRadius: 8,
            padding: 8,
            minHeight: 140,
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            color: "#666"
          }}
        >
          No recent transactions to show.
        </div>
      );
    }

    return (
      <div style={{ marginTop: 8, background:"#fafafa", border:"1px solid #eee", borderRadius:8, padding:8 }}>
        <div style={{ fontSize: 12, color:"#666", marginBottom: 6 }}>Recent transactions</div>
        <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:6 }}>
          {rows.map(r => {
            const ccy = r?.iso_currency_code || "USD";
            const raw = Number(r?.amount || 0);
            const display = raw * (signNormalization || 1);
            const d = r?.date ? new Date(r.date).toLocaleDateString() : "—";
            const vendor = r?.name || r?.merchant_name || "(no name)";
            return (
              <li key={r.id} style={{ display:"grid", gridTemplateColumns:"1fr auto auto", gap:8, alignItems:"center" }}>
                <div style={{ minWidth: 0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{vendor}</div>
                <div style={{ fontSize:12, color:"#666" }}>{d}</div>
                <div style={{ fontWeight: 700, color: display < 0 ? "#b00020" : "#0d7a3a" }}>
                  {new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(display)}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  });

  return (
    <div style={cardStyle}>
      {activeRebuildJob &&
        (activeRebuildJob.status === "queued" || activeRebuildJob.status === "running") && (
          <div
            style={{
              position: "fixed",
              top: 16,
              right: 16,
              zIndex: 9999,
              background: "rgba(255,255,255,0.95)",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "10px 12px",
              boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
              width: 320,
              fontSize: 13,
              lineHeight: 1.3,
            }}
          >
          </div>
        )}

      <h3 style={h3}>Plaid Connections</h3>

      {msg && <div style={noticeOk}>{msg}</div>}
      {err && <div style={noticeErr}>{err}</div>}

      {!hasAny && (
        <div style={{ background:"#fafafa", border:"1px solid #eee", borderRadius:8, padding:12, marginBottom:12 }}>
          No bank connections yet.
        </div>
      )}

            {hasAny && (
        <div style={{ display:"grid", gap: 12 }}>
          {items.map(it => {
            const status = it.connectionStatus || "ok";
            const code = it.errorCode || null;
            const accounts = Array.isArray(it.accounts) ? it.accounts : [];
            const shortId = (it.item_id || it.id || "").slice(-8);

            return (
              <div key={it.id} style={{ border:"1px solid #eee", borderRadius:12, overflow:"hidden" }}>
                {/* Header row: Institution + status + actions */}
                <div style={{
                  display:"grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr auto auto",
                  gap: isMobile ? 6 : 10,
                  alignItems:"center",
                  background:"#fafafa",
                  borderBottom:"1px solid #eee",
                  padding:"10px 12px",
                  rowGap: isMobile ? 8 : 10
                }}>
                  <div style={{ display:"flex", flexDirection:"column" }}>
                    <div style={{ fontWeight:700 }}>
                      {it.institution_name || "Institution"}
                    </div>
                    <div style={{ fontSize:12, color:"#666" }}>
                      Item • <code>{shortId}</code>
                    </div>
                  </div>
                  <div>{statusPill(status, code)}</div>
                  <div style={{ display:"flex", gap:12, justifyContent:"flex-end" }}>
                    <PlaidLinkButton
                      label="Update"
                      mode="update"
                      itemId={it.id}
                      style={{
                        background:"transparent",
                        border:"none",
                        color:"#111",
                        padding:0,
                        textDecoration:"underline",
                        cursor:"pointer",
                        height:"auto",
                      }}
                      onLinked={refreshAccounts}
                    />
                    <button
                      onClick={() => deleteOneItem(it.id)}
                      style={{
                        background:"transparent",
                        border:"none",
                        color:"#b00020",
                        padding:0,
                        textDecoration:"underline",
                        cursor:"pointer"
                      }}
                      title="Remove this connection"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {accounts.length === 0 ? (
                  <div style={{ padding:12 }}>
                    <div style={{ background:"#fff", padding:10, border:"1px solid #eee", borderRadius:8, color:"#444" }}>
                      {isFreshItem(it)
                        ? "Accounts take a few minutes to update and will populate here soon."
                        : "No accounts listed for this connection."}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding:12 }}>
                    {isMobile ? (
                      // Mobile: card list
                      <div style={{ display:"grid", gap:10 }}>
                        {accounts.map(a => {
                          const { amount, label } = pickBalance(a?.balances || {});
                          const ccy = a?.balances?.iso_currency_code || a?.iso_currency_code || "USD";
                          const subtype = a?.subtype || a?.type || "—";
                          const asof = fmtWhen(a?.updatedAt);
                          return (
                            <div key={a.id} style={{ border:"1px solid #eee", borderRadius:10, padding:10, display:"grid", gap:8 }}>
                              <div>
                                <div style={{ fontWeight:700 }}>{a?.name || "—"}</div>
                                <div style={{ fontSize:12, color:"#666" }}>
                                  {a?.mask ? <>•••• {a.mask}</> : null}
                                  {a?.official_name ? <> · {a.official_name}</> : null}
                                </div>
                              </div>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                                <div style={{ fontSize:13, color:"#666" }}>{subtype}</div>
                                <div style={{ textAlign:"right" }}>
                                  {typeof amount === "number" ? (
                                    <>
                                      <div style={{ fontWeight:700 }}>{fmtMoney(amount, ccy)}</div>
                                      <div style={{ fontSize:12, color:"#666" }}>as of {asof}{label ? ` (${label})` : ""}</div>
                                    </>
                                  ) : "—"}
                                </div>
                              </div>
                              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                                <button
                                  style={btnHollow}
                                  onClick={() => setFlipOpenFor({ item: it, account: a })}
                                  title="Adjust how signs display for this account"
                                >
                                  Flip signs
                                </button>
                                <button
                                  style={{ ...btnHollow, borderColor:"#bbb", color:"#333" }}
                                  onClick={() => toggleInline(a.id)}
                                  title="Show a few recent transactions from this account"
                                >
                                  {inlineOpen[a.id] ? "Hide sample" : "Show sample"}
                                </button>
                              </div>
                              <div
                                style={{ display: inlineOpen[a.id] ? "block" : "none" }}
                              >
                                <InlineSample
                                  user={user}
                                  accountId={a.id}
                                  signNormalization={typeof a?.signNormalization === "number" ? a.signNormalization : 1}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      // Desktop: keep table
                      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                        <colgroup>
                          <col style={{ width: "60%" }} />
                          <col style={{ width: "20%" }} />
                          <col style={{ width: "20%" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th style={thLeft}>Account</th>
                            <th style={th}>Subtype</th>
                            <th style={th}>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accounts.map(a => {
                            const { amount, label } = pickBalance(a?.balances || {});
                            const ccy = a?.balances?.iso_currency_code || a?.iso_currency_code || "USD";
                            const subtype = a?.subtype || a?.type || "—";
                            const asof = fmtWhen(a?.updatedAt);

                            return (
                              <React.Fragment key={a.id}>
                                {/* Account row */}
                                <tr>
                                  <td style={tdLeft}>
                                    <div style={{ fontWeight: 600 }}>{a?.name || "—"}</div>
                                    <div style={{ fontSize: 12, color: "#666" }}>
                                      {a?.mask ? <>•••• {a.mask}</> : null}
                                      {a?.official_name ? <> · {a.official_name}</> : null}
                                    </div>

                                    <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                      <button
                                        style={btnHollow}
                                        onClick={() => setFlipOpenFor({ item: it, account: a })}
                                        title="Adjust how signs display for this account"
                                      >
                                        Flip signs
                                      </button>
                                      <button
                                        style={{ ...btnHollow, borderColor: "#bbb", color: "#333" }}
                                        onClick={() => setInlineOpen(prev => ({ ...prev, [a.id]: !prev[a.id] }))}
                                        title="Show a few recent transactions from this account"
                                      >
                                        {inlineOpen[a.id] ? "Hide sample" : "Show sample"}
                                      </button>
                                    </div>
                                  </td>

                                  <td style={tdCenter}>{subtype}</td>
                                  <td style={tdCenter}>
                                    {typeof amount === "number" ? (
                                      <>
                                        <div style={{ fontWeight: 700 }}>{fmtMoney(amount, ccy)}</div>
                                        <div style={{ fontSize: 12, color: "#666" }}>
                                          as of {asof}{label ? ` (${label})` : ""}
                                        </div>
                                      </>
                                    ) : "—"}
                                  </td>
                                </tr>

                                {/* Expanded sample row — full width, outside the account cell */}
                                <tr>
                                  <td colSpan={3} style={{ padding: 0, background: "#fff" }}>
                                    <div
                                      style={{
                                        display: inlineOpen[a.id] ? "block" : "none",
                                        padding: "8px 8px 12px 8px"
                                      }}
                                    >
                                      <InlineSample
                                        user={user}
                                        accountId={a.id}
                                        signNormalization={
                                          typeof a?.signNormalization === "number" ? a.signNormalization : 1
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>

                                {/* Thin separator row */}
                                <tr>
                                  <td colSpan={3} style={{ padding: 0, borderBottom: "1px solid #f0f0f0" }} />
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add account */}
      <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <PlaidLinkButton
          label={hasAny ? "Add Another Account" : "Add Account"}
          mode="create"
          style={{
            background: "#00B074",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "10px 14px",
            fontWeight: 700,
            cursor: "pointer",
            height: 40,
          }}
          onLinked={refreshAccounts}
        />
        <button
          onClick={refreshAccounts}
          disabled={backfillBusy}
          style={{
            background: "#F4C542",
            color: "#111",
            border: "none",
            borderRadius: 10,
            padding: "10px 14px",
            fontWeight: 700,
            cursor: backfillBusy ? "not-allowed" : "pointer",
            height: 40,
            opacity: backfillBusy ? 0.7 : 1,
          }}
          title="Fetch accounts & balances for all linked institutions"
          type="button"
        >
          {backfillBusy ? "Refreshing…" : "Refresh Accounts"}
        </button>
        {items.some(it => it.connectionStatus === "error") && (
          <small style={{ color: "#8a1f1f" }}>
            Tip: click <strong>Update</strong> beside a connection flagged as broken.
          </small>
        )}
      </div>

      {flipOpenFor && (
        <FlipSignsModal
          open={!!flipOpenFor}
          onClose={() => setFlipOpenFor(null)}
          onAfterSave={handleAfterFlipSigns}
          user={user}
          account={flipOpenFor.account}
          currentSign={
            typeof flipOpenFor.account?.signNormalization === "number"
              ? flipOpenFor.account.signNormalization
              : 1
          }
          confirmedSign={
            typeof flipOpenFor.account?.confirmedSignNormalization === "number"
              ? flipOpenFor.account.confirmedSignNormalization
              : null
          }
          isConfirmed={!!flipOpenFor.account?.userConfirmed || !!flipOpenFor.account?.userOverridden}
        />
      )}

    </div>
  );
}

/** --------------------
 * Shared UI bits
 * -------------------- */
function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 12, color: "#666" }}>{label}</span>
      {children}
    </label>
  );
}

const h3 = { margin: 0, marginBottom: 10, fontSize: "1.05rem" };
const h4 = { margin: 0, marginBottom: 8, fontSize: "1rem" };
const input = {
  height: 38,
  borderRadius: 8,
  border: "1px solid #ddd",
  padding: "0 12px",
  fontSize: "0.95rem",
};
const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "0.75rem",
  alignItems: "center",
};
const cardStyle = {
  background: "white",
  border: "1px solid #eee",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
};
const divider = { height: 1, background: "#eee", margin: "12px 0" };
const btnPrimary = {
  background: PRIMARY,
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};
const btnSec = {
  background: ACCENT,
  color: "#333",
  border: "none",
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};
const th = { borderBottom: "2px solid #eee", padding: "10px 8px", textAlign: "center", fontWeight: 700 };
const thLeft = { ...th, textAlign: "left" };
const tdCenter = { borderBottom: "1px solid #f0f0f0", padding: "8px 8px", textAlign: "center" };
const tdLeft = { ...tdCenter, textAlign: "left" };

const btnHollow = {
  background: "transparent",
  color: PRIMARY,
  border: `1px solid ${PRIMARY}`,
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

const noticeOk = {
  background: "#f0fff6",
  border: "1px solid #c8f1de",
  color: "#0f6848",
  padding: "8px 10px",
  borderRadius: 8,
  marginBottom: 10,
};

const noticeErr = {
  background: "#fff5f5",
  border: "1px solid #ffd1d1",
  color: "#8a1f1f",
  padding: "8px 10px",
  borderRadius: 8,
  marginBottom: 10,
};

function TwoFAModal({ open, onClose, user }) {
  console.log("[2FA] MODAL MOUNT v-2FA-LOGS-A");
  const [tab, setTab] = useState("list"); // 'list' | 'totp' | 'sms'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [enrolled, setEnrolled] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  // Debug helper so every setEnrolled prints what we set
  function applyEnrolled(list, label) {
    const count = Array.isArray(list) ? list.length : "(not array)";
    console.debug("[2FA] applyEnrolled", label, "count=", count, "sample=", Array.isArray(list) ? list[0] : list);
    setEnrolled(list);
  }

  const emailVerified = !!user?.emailVerified;

  // SMS state
  const [smsPhone, setSmsPhone] = useState("");
  const [smsLabel, setSmsLabel] = useState("Phone");
  const [verificationId, setVerificationId] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const appVerifierRef = useRef(null); // holds RecaptchaVerifier instance

  const [needReauth, setNeedReauth] = useState(false);
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthErr, setReauthErr] = useState("");
  const pendingActionRef = useRef(null);

  // Authoritative fallback: ask Identity Toolkit for MFA enrollments
  async function fetchEnrolledFactorsViaRest() {
    try {
      const u = auth.currentUser;
      if (!u) return [];
      const idToken = await u.getIdToken(true);
      const resp = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${firebaseConfig.apiKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        }
      );
      if (!resp.ok) return [];
      const data = await resp.json();
      const infos = data?.users?.[0]?.mfaInfo || [];
      const mapped = infos.map(i => ({
        uid: i.mfaEnrollmentId,
        factorId: "phone",
        displayName: i.displayName || "Phone",
        phoneInfo: i.phoneInfo,
      }));
      console.log("[2FA] REST enrollments", { count: mapped.length, first: mapped[0] });
      return mapped;
      } catch (e) {
        console.debug("[2FA] REST error", e?.message || e);
        return [];
      }
  }

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        console.log("[2FA] open effect start", { open, reloadTick, userUid: user?.uid });
        const fresh = auth.currentUser;
        if (!fresh) { applyEnrolled([], "open:no-user"); return; }

        await fresh.reload();
        const sdkList = fresh.multiFactor?.enrolledFactors || [];
        console.log("[2FA] SDK enrolledFactors", { count: sdkList.length, first: sdkList[0] });
        if (sdkList.length > 0) {
          applyEnrolled(sdkList, "open:sdk");
          return;
        }

      // SDK returned 0, but we know REST may have truth — try it:
      const restList = await fetchEnrolledFactorsViaRest();
      console.log("[2FA] REST result", { count: restList.length, first: restList[0] });
      applyEnrolled(restList, "open:rest");
      } catch (e) {
        console.debug("[2FA] open effect error", e?.message || e);
        applyEnrolled([], "open:error");
      }
    })();
  }, [open, reloadTick]);

  function resetErrors() { setError(""); }

  useEffect(() => {
    if (!open) return;

    // If already created for this open session, don't recreate.
    if (appVerifierRef.current) return;

    (async () => {
      try {
        appVerifierRef.current = new RecaptchaVerifier(
          auth,
          "mfa-recaptcha",       // use the stable container ID
          { size: "invisible" }  // you can change to 'normal' for debugging
        );
        await appVerifierRef.current.render();
        setRecaptchaReady(true);
      } catch (e) {
        setRecaptchaReady(false);
        setError("reCAPTCHA init failed. Ensure Phone sign-in is enabled and you’re on an authorized domain (https).");
      }
    })();

    // Cleanup when modal closes
    return () => {
      try {
        if (appVerifierRef.current) {
          appVerifierRef.current.clear();
          appVerifierRef.current = null;
        }
      } catch {}
      setRecaptchaReady(false);
    };
  }, [open]);

  async function startSmsEnroll() {
    setError("");

    // Normalize to E.164
    const raw = (smsPhone || "").trim();
    const digits = raw.replace(/\D+/g, "");
    let e164 = "";
    if (/^\+\d{8,15}$/.test(raw)) {
      e164 = raw;
    } else if (digits.length === 10) {
      e164 = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith("1")) {
      e164 = `+${digits}`;
    } else if (digits.length >= 8 && digits.length <= 15) {
      e164 = `+${digits}`;
    }

    if (!e164) {
      setError("Enter a valid phone number (e.g. +15551234567 or 678-555-1234).");
      return;
    }
    setSmsPhone(e164);

    if (!appVerifierRef.current) {
      setError("reCAPTCHA not ready yet.");
      return;
    }

    setLoading(true);
    try {
      const session = await multiFactor(user).getSession(); // ← may throw requires-recent-login
      const provider = new PhoneAuthProvider(auth);

      const vid = await provider.verifyPhoneNumber(
        { phoneNumber: e164, session },
        appVerifierRef.current
      );
      setVerificationId(vid);
    } catch (e) {
      // ⬇️ NEW: catch recent-login and prompt for password
      if (e?.code === "auth/requires-recent-login") {
        setNeedReauth(true);
        setReauthPassword("");
        setReauthErr("");
        pendingActionRef.current = () => startSmsEnroll(); // retry after reauth
      } else {
        setError(e?.message || String(e));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleReauth() {
    if (!user?.email) {
      setReauthErr("No email on this account.");
      return;
    }
    setReauthErr("");
    try {
      const cred = EmailAuthProvider.credential(user.email, reauthPassword);
      await reauthenticateWithCredential(user, cred);
      setNeedReauth(false);
      setReauthPassword("");
      // Retry the action that needed reauth
      if (typeof pendingActionRef.current === "function") {
        const fn = pendingActionRef.current;
        pendingActionRef.current = null;
        await fn();
      }
    } catch (e) {
      setReauthErr(e?.message || String(e));
    }
  }

  async function confirmSmsEnroll() {
    if (!verificationId || !smsCode) return;
    setLoading(true); resetErrors();
    try {
      const cred = PhoneAuthProvider.credential(verificationId, smsCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(user).enroll(assertion, smsLabel || "Phone");
      try {
        await auth.currentUser?.reload();
      } catch {}
            try {
        await setDoc(
          doc(db, "users", user.uid),
          {
            phone: smsPhone,
            mfaEnrolled: true,
            mfaEnrolledAt: serverTimestamp(),
            updatedAt: new Date(),
          },
          { merge: true }
        );
      } catch {}
      setVerificationId("");
      setSmsCode("");
      setTab("list");
      // bump the tick to re-run the open-effect too
      setReloadTick(t => t + 1);
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  }

  async function removeFactor(uid) {
    setLoading(true); resetErrors();
    try {
      await multiFactor(user).unenroll(uid);
    try {
      await auth.currentUser?.reload();
    } catch {}
    setReloadTick(t => t + 1);
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  }

  if (!open) return null;

  return (
    <div style={modalBackdrop}>
      <div style={modalCard}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h3 style={{ margin:0 }}>Two-Factor Authentication <span style={{fontSize:12,color:"#999"}}>• v-2FA-LOGS-A</span></h3>
          <button onClick={onClose} style={btnHollow}>Close</button>
        </div>

        {/* Always-mounted, hidden reCAPTCHA anchor */}
        <div
          id="mfa-recaptcha"
          style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }}
        />

        {error && <div style={noticeErr} role="alert">{error}</div>}

        {/* Tabs */}
        <div style={{ display:"flex", gap:8, margin:"12px 0" }}>
          <button
            onClick={() => setTab("list")}
            style={{ ...tabBtn, ...(tab==="list"?tabBtnActive:{} ) }}
          >Enrolled Factors</button>
          <button
            onClick={() => {
              if (!emailVerified) { setError("Verify your email before enrolling 2FA."); return; }
              setTab("sms");
            }}
            style={{ ...tabBtn, ...(tab==="sms"?tabBtnActive:{} ) }}
          >Add SMS</button>
        </div>

        {/* Content */}
        {tab === "list" && (
          <div>
            <p style={{ marginTop:0 }}>
              At least one 2FA method is strongly recommended for Trelli.
            </p>

            <div style={{ margin: "8px 0 12px" }}>
              <button
                style={btnHollow}
                onClick={async () => {
                  try {
                    const fresh = auth.currentUser;
                    if (!fresh) { applyEnrolled([], "resync:no-user"); return; }
                    await fresh.reload();
                    const sdkList = fresh.multiFactor?.enrolledFactors || [];
                    console.log("[2FA] Resync SDK enrolledFactors", { count: sdkList.length, first: sdkList[0] });
                    if (sdkList.length > 0) {
                      applyEnrolled(sdkList, "resync:sdk");
                      return;
                    }
                  const restList = await fetchEnrolledFactorsViaRest();
                  applyEnrolled(restList, "resync:rest");
                  } catch (e) {
                    setError(e?.message || String(e));
                  }
                }}
              >
                Resync enrolled factors
              </button>
            </div>

            {enrolled.length === 0 ? (
              <div style={{ background:"#fafafa", padding:12, borderRadius:8, border:"1px solid #eee" }}>
                No factors enrolled yet.
              </div>
            ) : (
              <ul style={{ listStyle:"none", padding:0, margin:0, display:"grid", gap:8 }}>
                {enrolled.map(f => (
                  <li key={f.uid} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", border:"1px solid #eee", borderRadius:8, padding:"10px 12px" }}>
                    <div>
                      <div><strong>{f.displayName || f.factorId}</strong></div>
                        <small>
                          Type: {f.factorId}{f.phoneInfo ? ` · ${f.phoneInfo}` : ""}
                        </small>
                    </div>
                    <button onClick={() => removeFactor(f.uid)} style={btnHollow} disabled={loading}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "sms" && (
          <div>
            <div style={{ display:"grid", gap:10 }}>
              {needReauth && (
                <div style={{
                  background:"#fff8e1",
                  border:"1px solid #f6d67d",
                  color:"#6b4e00",
                  padding:12,
                  borderRadius:8
                }}>
                  <div style={{ fontWeight:700, marginBottom:6 }}>
                    For security, please confirm your password to continue.
                  </div>

                  {reauthErr && (
                    <div style={{
                      background:"#fff5f5",
                      border:"1px solid #ffd1d1",
                      color:"#8a1f1f",
                      padding:"6px 8px",
                      borderRadius:6,
                      marginBottom:8
                    }}>
                      {reauthErr}
                    </div>
                  )}

                  <input
                    type="password"
                    placeholder="Your account password"
                    style={input}
                    value={reauthPassword}
                    onChange={(e) => setReauthPassword(e.target.value)}
                  />
                  <div style={{ display:"flex", gap:8, marginTop:8 }}>
                    <button
                      onClick={handleReauth}
                      style={btnPrimary}
                      disabled={!reauthPassword || loading}
                    >
                      Verify & Retry
                    </button>
                    <button
                      onClick={() => {
                        setNeedReauth(false);
                        setReauthPassword("");
                        setReauthErr("");
                      }}
                      style={btnHollow}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              <label style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <span style={{ fontSize:12, color:"#666" }}>Phone (E.164 format)</span>
                <input style={input} placeholder="+15551234567" value={smsPhone} onChange={e=>setSmsPhone(e.target.value)} />
              </label>
              <label style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <span style={{ fontSize:12, color:"#666" }}>Label</span>
                <input style={input} placeholder="Phone" value={smsLabel} onChange={e=>setSmsLabel(e.target.value)} />
              </label>

              {!verificationId ? (
                <button onClick={startSmsEnroll} style={btnPrimary} disabled={loading || !smsPhone || !recaptchaReady || needReauth}>
                  Send Code
                </button>
              ) : (
                <>
                  <input style={input} placeholder="SMS code" value={smsCode} onChange={e=>setSmsCode(e.target.value)} />
                  <button onClick={confirmSmsEnroll} style={btnPrimary} disabled={loading || !smsCode}>Confirm & Enroll</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FlipSignsModal({ open, onClose, onAfterSave, user, account, currentSign, confirmedSign, isConfirmed }) {
  const [loading, setLoading] = useState(false);
  const [sample, setSample] = useState([]); // raw Plaid mirror docs
  const [previewSign, setPreviewSign] = useState(currentSign || 1);
  const [applyScope, setApplyScope] = useState("all"); // 'past' | 'future' | 'all'
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const signMismatch =
  typeof confirmedSign === "number" &&
  typeof currentSign === "number" &&
  confirmedSign !== currentSign;

  // fetch a few recent raw txns for this account to preview
  useEffect(() => {
    if (!open || !user?.uid || !account?.id) return;
    setErr(""); setMsg("");
    // keep existing rows to avoid table flash while refetching
    (async () => {
      try {
        const { getDocs, query: fsQuery, where, limit, collection } = await import("firebase/firestore");
        // No orderBy → no index required
        const q = fsQuery(
          collection(db, "users", user.uid, "plaid", "transactions", "byId"),
          where("account_id", "==", account.id),
          limit(50)
        );
        const snap = await getDocs(q);
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const da = a?.date ? new Date(a.date).getTime() : 0;
          const dbt = b?.date ? new Date(b.date).getTime() : 0;
          return dbt - da;
        });
        setSample(rows.slice(0, 10));
        setPreviewSign(currentSign || 1);
      } catch (e) {
        setErr(e?.message || String(e));
      }
    })();
  }, [open, user?.uid, account?.id, currentSign]);

  function fmt(n, ccy = "USD") {
    const v = Number(n || 0);
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(v); }
    catch { return `${v.toFixed(2)} ${ccy}`.trim(); }
  }

  // Compute preview amounts strictly by account-level rule:
  // amount = raw * previewSign
  function previewAmount(raw) {
    const v = Number(raw || 0) * (previewSign || 1);
    return v;
  }

  async function handleSave() {
    if (!user?.uid || !account?.id) return;
    setLoading(true); setErr(""); setMsg("");
    try {
      // A) Durable override FIRST
      await setDoc(
        doc(db, "users", user.uid, "plaid", "accounts", "byId", account.id),
        {
          // Canonical lock fields (match wizard contract)
          confirmedSignNormalization: previewSign,
          signNormalization: previewSign,
          userConfirmed: true,
          userOverridden: true,

          // Debugging / audit
          __writer: "settings:flipSignsModal",
          updatedAt: new Date(),
        },
        { merge: true }
      );

      // B) Then mirror onto byId and mark sticky
      await setDoc(
        doc(db, "users", user.uid, "plaid", "accounts", "byId", account.id),
        {
          signNormalization: previewSign,
          userOverridden: true,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      // (optional) If you already call your rebuild function here, keep it.
      // Make sure it runs AFTER the two writes above so the backend sees the override.

      onAfterSave?.({ accountId: account.id, sign: previewSign, scope: applyScope });
      onClose?.({ changed: true });
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div style={modalBackdrop}>
      <div style={modalCard}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h3 style={{ margin:0 }}>Flip Signs for <span style={{ color: PRIMARY }}>{account?.name || "Account"}</span></h3>
          <button onClick={() => onClose?.({ changed: false })} style={btnHollow}>Cancel</button>
        </div>

        <div style={{ marginTop: 8, display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          {isConfirmed && !signMismatch && (
            <span style={{
              background:"#f0fff6",
              border:"1px solid #c8f1de",
              color:"#0f6848",
              padding:"4px 10px",
              borderRadius:999,
              fontSize:12,
              fontWeight:700
            }}>
              Confirmed
            </span>
          )}

          {signMismatch && (
            <span style={{
              background:"#fff5f5",
              border:"1px solid #ffd1d1",
              color:"#8a1f1f",
              padding:"4px 10px",
              borderRadius:999,
              fontSize:12,
              fontWeight:800
            }}>
              Sign mismatch detected (needs fixing)
            </span>
          )}
        </div>


        {err && <div style={{ ...noticeErr, marginTop: 10 }}>{err}</div>}
        {msg && <div style={{ ...noticeOk,  marginTop: 10 }}>{msg}</div>}

        <div style={{ marginTop: 12, display:"flex", gap: 12, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ padding:"6px 10px", border:"1px solid #eee", borderRadius: 10, background:"#fafafa" }}>
            <strong>Preview</strong>: This affects how amounts display for this account only.
          </div>
          <button
            style={btnHollow}
            onClick={() => setPreviewSign(s => (s === 1 ? -1 : 1))}
            title="Toggle how amounts will display for this account"
          >
            Switch signs in preview
          </button>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display:"flex", gap:10, alignItems:"center" }}>
            <span style={{ fontSize: 13, color:"#666" }}>Apply to:</span>
            <select
              value={applyScope}
              onChange={(e) => setApplyScope(e.target.value)}
              style={{ ...input, height: 34, padding: "0 10px", width: 220 }}
            >
              <option value="all">Past & Future</option>
              <option value="past">Past only</option>
              <option value="future">Future only</option>
            </select>
          </label>
          <small style={{ color:"#666" }}>
            “Future only” updates the account setting without rewriting existing transactions.
          </small>
        </div>

        <div style={{ marginTop: 14 }}>
          <h4 style={h4}>Preview (does not save yet)</h4>
          <div
            style={{
              marginTop: 8,
              border: "1px solid #eee",
              borderRadius: 8,
              padding: 10,
              minHeight: 220,                  // ← keeps height stable
              overflowX: "auto"
            }}
          >
            {sample.length === 0 ? (
              <div style={{ background:"#fafafa", color:"#666", borderRadius: 6, padding: 10 }}>
                No recent transactions found for preview.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "50%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "17%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={thLeft}>Vendor</th>
                    <th style={th}>Date</th>
                    <th style={th}>Raw</th>
                    <th style={th}>Preview Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {sample.map(t => {
                    const ccy = t?.iso_currency_code || "USD";
                    const raw = Number(t?.amount || 0);
                    const p = previewAmount(raw);
                    const d = t?.date ? new Date(t.date).toLocaleDateString() : "—";
                    const vendor = t?.name || t?.merchant_name || "(no name)";
                    return (
                      <tr key={t.id}>
                        <td style={tdLeft}><div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{vendor}</div></td>
                        <td style={tdCenter}>{d}</td>
                        <td style={tdCenter}>{fmt(raw, ccy)}</td>
                        <td style={{ ...tdCenter, color: p < 0 ? "#b00020" : "#0d7a3a", fontWeight: 700 }}>
                          {fmt(p, ccy)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16, display:"flex", gap: 10, justifyContent:"flex-end" }}>
          <button style={btnHollow} onClick={() => onClose?.({ changed: false })}>Cancel</button>
          <button style={btnPrimary} onClick={handleSave} disabled={loading}>
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Modal styles (simple) */
const modalBackdrop = {
  position:"fixed", inset:0, background:"rgba(0,0,0,0.25)",
  display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999
};
const modalCard = {
  width:"min(720px, 92vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  background:"#fff",
  borderRadius:12,
  padding:16,
  border:"1px solid #eee",
  boxShadow:"0 10px 30px rgba(0,0,0,0.15)"
};
const tabBtn = {
  background:"transparent", border:"1px solid #ddd", borderRadius:10,
  padding:"8px 10px", cursor:"pointer"
};
const tabBtnActive = { borderColor: PRIMARY, color: PRIMARY, fontWeight:700 };

// Toggle for MVP: disable Users UI but keep code in place
const USERS_FEATURE_ENABLED = false;

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // initialize from query (?tab=account|users|plaid|notifications)
  const allowedTabs = new Set(["account", "users", "plaid", "notifications"]);
  const qsTabInit = (() => {
    const t = new URLSearchParams(location.search).get("tab") || "account";
    return allowedTabs.has(t) ? t : "account";
  })();

  const [tab, setTab] = useState(qsTabInit);

  // keep state in sync when ?tab= changes elsewhere
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab") || "account";
    if (allowedTabs.has(t) && t !== tab) setTab(t);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // when the user clicks tabs, also update the URL (no full reload)
  const setTabAndQuery = (next) => {
    if (!allowedTabs.has(next)) return;
    if (next !== tab) setTab(next);
    const params = new URLSearchParams(location.search);
    params.set("tab", next);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        paddingInline: "16px",
        paddingTop: "3rem",
        maxWidth: 1120,
        marginInline: "auto"
      }}
    >
      <h2 style={{ marginTop: 0 }}>Settings</h2>
      <TopTabs tab={tab} setTab={setTabAndQuery} />
      {tab === "account" && <AccountSection />}
      {tab === "users" && <UsersSection />}
      {tab === "plaid" && <PlaidConnectionsSection />}
      {tab === "notifications" && <NotificationsSection />}
    </div>
  );
}
